import { AAR_ELEMENTS, aarElementsLabel } from '../shared/domain';
import type { ReportStatus } from '../shared/status';
import {
  deleteAar,
  downloadReportPdf,
  fetchAar,
  reviewReport,
  saveAar,
  uploadPhoto,
  type Aar,
  type AuthUser,
} from './api';
import { photoField, photoGallery } from './photos';
import { bylines, el, fmtTime, formatPlace, fromLocalInput, go, statusPill, textarea, toast, toLocalInput } from './ui';
import type { PendingPhoto } from './offline';

const TOPIC_PROMPT =
  'What specific capability, action, process, condition, deficiency, or best practice am I addressing? Can I describe it clearly in one line?';
const DISCUSSION_PROMPT =
  'What happened? What was supposed to happen? What evidence supports the observation? Why did it happen? What conditions contributed? What effect did it have on mission accomplishment, effectiveness, risk, or efficiency? Is this an isolated occurrence or potentially a recurring/systemic issue?';
const RECOMMENDATION_PROMPT =
  'What specifically should be changed, corrected, institutionalized, or sustained? Who should take action? What would the improved condition look like? Is the recommendation feasible and directly supported by the discussion?';

export function renderAar(root: HTMLElement, opts: { user: AuthUser; aarId?: string }): () => void {
  const photos = photoField();
  let existing: Aar | null = null;
  let clientId = crypto.randomUUID();
  let elements: string[] = [];
  let alive = true;
  let coords: { lat: number | null; lng: number | null; accuracy: number | null } = {
    lat: null,
    lng: null,
    accuracy: null,
  };

  const when = el('input', {
    className: 'input',
    type: 'datetime-local',
    value: toLocalInput(new Date()),
  }) as HTMLInputElement;
  const place = el('input', {
    className: 'input',
    placeholder: 'Where this happened',
  }) as HTMLInputElement;
  const elementRow = el('div', { className: 'chips' });
  const topic = textarea('');
  topic.rows = 3;
  const discussion = textarea('');
  discussion.rows = 6;
  const recommendation = textarea('');
  recommendation.rows = 5;
  const error = el('p', { className: 'form-error' });
  const save = el('button', { type: 'button', className: 'btn', textContent: 'Save' });
  const submit = el('button', { type: 'button', className: 'btn btn-primary', textContent: 'Submit' });

  function paintElements(): void {
    elementRow.replaceChildren();
    for (const item of AAR_ELEMENTS) {
      const on = elements.includes(item.id);
      const btn = el('button', {
        type: 'button',
        className: on ? 'chip on' : 'chip',
        textContent: item.label,
        'aria-pressed': on ? 'true' : 'false',
      });
      btn.addEventListener('click', () => {
        elements = on ? elements.filter((id) => id !== item.id) : [...elements, item.id];
        paintElements();
      });
      elementRow.append(btn);
    }
  }

  function problem(status: 'draft' | 'pending'): string {
    if (!when.value) return 'Set the time';
    if (!elements.length) return 'Pick an element';
    if (status === 'draft') return '';
    if (!place.value.trim()) return 'Describe the place';
    if (!topic.value.trim()) return 'Add the AAR topic';
    if (!discussion.value.trim()) return 'Add the discussion';
    if (!recommendation.value.trim()) return 'Add the recommendation';
    return '';
  }

  function payload(keys: string[], status: 'draft' | 'pending'): Record<string, unknown> {
    return {
      client_id: clientId,
      observed_at: fromLocalInput(when.value),
      element: elements[0] || '',
      elements,
      location_lat: coords.lat,
      location_lng: coords.lng,
      location_accuracy_m: coords.accuracy,
      location_description: place.value.trim(),
      topic: topic.value.trim(),
      discussion: discussion.value.trim(),
      recommendation: recommendation.value.trim(),
      status,
      evidence_keys: keys,
    };
  }

  async function onSubmit(status: 'draft' | 'pending'): Promise<void> {
    const message = problem(status);
    error.textContent = message;
    if (message) return;
    save.disabled = true;
    submit.disabled = true;
    const pending = photos.pending();
    const keys = [...photos.keptKeys()];
    const remaining: PendingPhoto[] = [];
    let failed: unknown = null;
    for (const photo of pending) {
      if (failed) {
        remaining.push(photo);
        continue;
      }
      try {
        keys.push(
          await uploadPhoto(new Blob([photo.data], { type: photo.contentType }), photo.contentType, photo.filename)
        );
      } catch (err) {
        failed = err;
        remaining.push(photo);
      }
    }
    try {
      if (failed) throw failed;
      const saved = await saveAar(payload(keys, status), existing?.id);
      existing = saved;
      clientId = saved.client_id || clientId;
      photos.setExisting(saved.evidence_keys || []);
      photos.setPending([]);
      toast(status === 'draft' ? 'Saved' : 'Submitted for review', 'ok');
      sessionStorage.setItem('mdrc-board-filter', 'aar');
      go('board');
    } catch (err) {
      photos.setExisting(keys);
      photos.setPending(remaining);
      error.textContent = (err as Error).message;
      toast((err as Error).message, 'err');
    } finally {
      if (alive) {
        save.disabled = false;
        submit.disabled = false;
      }
    }
  }

  function apply(aar: Aar): void {
    existing = aar;
    clientId = aar.client_id || clientId;
    when.value = toLocalInput(new Date(aar.observed_at));
    elements = aar.elements?.length ? [...aar.elements] : aar.element ? [aar.element] : [];
    coords = {
      lat: aar.location_lat,
      lng: aar.location_lng,
      accuracy: aar.location_accuracy_m,
    };
    place.value = aar.location_description || '';
    topic.value = aar.topic;
    discussion.value = aar.discussion;
    recommendation.value = aar.recommendation;
    photos.setExisting(aar.evidence_keys || []);
    paintElements();
  }

  save.addEventListener('click', () => void onSubmit('draft'));
  submit.addEventListener('click', () => void onSubmit('pending'));
  paintElements();
  root.append(
    el('h1', { textContent: opts.aarId ? 'Edit AAR' : 'AAR' }),
    el('div', { className: 'aar-form' }, [
      el('label', { className: 'field' }, [el('span', { className: 'field-label', textContent: 'When' }), when]),
      el('div', { className: 'block' }, [el('span', { className: 'field-label', textContent: 'Element' }), elementRow]),
      el('label', { className: 'field' }, [el('span', { className: 'field-label', textContent: 'Place' }), place]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'AAR Topic' }),
        el('span', { className: 'field-hint', textContent: TOPIC_PROMPT }),
        topic,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Discussion' }),
        el('span', { className: 'field-hint', textContent: DISCUSSION_PROMPT }),
        discussion,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Recommendation' }),
        el('span', { className: 'field-hint', textContent: RECOMMENDATION_PROMPT }),
        recommendation,
      ]),
      photos.element,
      error,
      el('div', { className: 'action-bar' }, [save, submit]),
    ])
  );

  if (opts.aarId) {
    void fetchAar(opts.aarId)
      .then((aar) => {
        if (alive) apply(aar);
      })
      .catch((err) => {
        if (alive) error.textContent = (err as Error).message;
      });
  }

  return () => {
    alive = false;
    photos.dispose();
  };
}

export function aarDetail(root: HTMLElement, opts: { user: AuthUser; id: string }): () => void {
  const host = el('div', { className: 'stack' });
  root.append(host);
  host.append(el('p', { className: 'muted', textContent: 'Loading…' }));
  let alive = true;

  async function paint(): Promise<void> {
    try {
      const aar = await fetchAar(opts.id);
      if (!alive) return;
      host.replaceChildren(
        el('p', { className: 'kicker' }, [
          statusPill(aar.status),
          el('span', { className: 'pill', textContent: 'AAR' }),
          el('span', { textContent: fmtTime(aar.observed_at) }),
        ]),
        el('h1', { textContent: aar.topic || 'AAR' }),
        el('p', {
          className: 'muted',
          textContent: [
            aarElementsLabel(aar.elements, aar.element),
            formatPlace(aar.location_lat, aar.location_lng, aar.location_description) || 'No place',
          ]
            .filter(Boolean)
            .join(' · '),
        }),
        ...bylines(aar),
        block('Discussion', aar.discussion),
        block('Recommendation', aar.recommendation),
        photoGallery(aar.evidence_keys)
      );
      const exportBtn = el('button', { type: 'button', className: 'btn', textContent: 'Export PDF' });
      exportBtn.addEventListener('click', () => {
        void downloadReportPdf('aars', aar.id).catch((err) => toast((err as Error).message, 'err'));
      });
      host.append(exportBtn);
      if (opts.user.role === 'admin') host.append(...reviewButtons(aar.status, paint));
      if (aar.author_id === opts.user.id || opts.user.role === 'admin') {
        const edit = el('button', { type: 'button', className: 'btn', textContent: 'Edit' });
        edit.addEventListener('click', () => go(`aar/${aar.id}`));
        let armed = false;
        const remove = el('button', { type: 'button', className: 'btn btn-ghost', textContent: 'Delete' });
        remove.addEventListener('click', () => {
          if (!armed) {
            armed = true;
            remove.textContent = 'Tap again to delete';
            return;
          }
          void deleteAar(aar.id)
            .then(() => go('board'))
            .catch((err) => toast((err as Error).message, 'err'));
        });
        host.append(edit, remove);
      }
    } catch (err) {
      if (alive) host.replaceChildren(el('p', { className: 'form-error', textContent: (err as Error).message }));
    }
  }

  function reviewButtons(status: ReportStatus, onDone: () => Promise<void>): HTMLButtonElement[] {
    return (
      [
        ['Return for Corrections', 'returned'],
        ['Approved', 'approved'],
      ] as const
    ).map(([label, review]) => {
      const btn = el('button', { type: 'button', className: 'btn', textContent: label });
      if (status === review) btn.disabled = true;
      btn.addEventListener('click', () => {
        btn.disabled = true;
        void reviewReport('aars', opts.id, review)
          .then(() => {
            toast(review === 'approved' ? 'Approved' : 'Returned for corrections', 'ok');
            return onDone();
          })
          .catch((err) => {
            btn.disabled = false;
            toast((err as Error).message, 'err');
          });
      });
      return btn;
    });
  }

  void paint();
  return () => {
    alive = false;
  };
}

function block(label: string, text: string): HTMLElement {
  return el('div', { className: 'block' }, [
    el('span', { className: 'field-label', textContent: label }),
    el('p', { textContent: text || '—' }),
  ]);
}
